import { MutableRefObject } from "react";

import { Flex, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { TextInput } from "components";
import { BankCreditOptionsResponse, BankCreditRequestForm, BankCreditResponse } from "types";

import { BankCreditRequestPersonalAddress } from "./bank-credit-address";
import { BankCreditRequestPersonalChildren } from "./bank-credit-children";
import { BankCreditRequestPersonalInfo } from "./bank-credit-info";
import { BankCreditRequestMarital } from "./bank-credit-marital";
import { BankCreditRequestPersonalPartner } from "./bank-credit-partner";

interface BankCreditRequestPersonalProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  options: BankCreditOptionsResponse;
  visibleFields: string[];
  sourceChildBirthYears?: Array<number | undefined>;
  readOnly?: boolean;
  updateCredit: (item: Partial<BankCreditRequestForm>) => Promise<void>;
  revalidateEmploymentDatesRef?: MutableRefObject<
    ((newBirthDate?: Date | undefined | null, newVisaFrom?: Date | undefined | null) => void) | null
  >;
}

export const BankCreditRequestPersonal = ({
  credit,
  form,
  options,
  visibleFields,
  sourceChildBirthYears,
  readOnly = false,
  updateCredit,
  revalidateEmploymentDatesRef,
}: BankCreditRequestPersonalProps) => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" p="md" gap="lg">
      <BankCreditRequestPersonalInfo
        credit={credit}
        form={form}
        options={options}
        visibleFields={visibleFields}
        readOnly={readOnly}
        updateCredit={updateCredit}
        revalidateEmploymentDatesRef={revalidateEmploymentDatesRef}
      />
      <BankCreditRequestPersonalAddress
        form={form}
        options={options}
        visibleFields={visibleFields}
        readOnly={readOnly}
      />
      <BankCreditRequestMarital
        form={form}
        options={options}
        visibleFields={visibleFields}
        readOnly={readOnly}
      />
      <BankCreditRequestPersonalChildren
        credit={credit}
        form={form}
        options={options}
        visibleFields={visibleFields}
        sourceChildBirthYears={sourceChildBirthYears}
        readOnly={readOnly}
      />
      {form.getValues().partnerIncludeInApplication && (
        <BankCreditRequestPersonalPartner
          form={form}
          options={options}
          visibleFields={visibleFields}
          readOnly={readOnly}
        />
      )}

      <SimpleGrid cols={4} mt="lg">
        <TextInput
          label={t("credit.comment.singular")}
          key={form.key("comment2")}
          {...form.getInputProps("comment2")}
          disabled={readOnly}
        />
      </SimpleGrid>
    </Flex>
  );
};
